/* eslint-disable consistent-return */
import Validation from "backbone-validation";
import moment from "moment-timezone";
import Fuse from "fuse.js";

import Base from "./base";
import { isGutCheckTest } from "../views/app/util";

const APPLICATION_STATE = {
    APPLIED: 0,
    INTERVIEW: 1,
    REJECTED: 2,
    HIRE_PENDING: 3,
    CANCELED: 4,
};

const REJECTION_REASON = {
    EDUCATION: 2 ** 1,
    AVAILABILITY: 2 ** 2,
    GAPS: 2 ** 3,
    LOCATION: 2 ** 4,
    QUESTION_ANSWERS: 2 ** 5,
    OVERQUALIFIED: 2 ** 6,
    POSITION_CLOSED: 2 ** 7,
    NO_SHOW: 2 ** 8,
    LATE: 2 ** 9,
    ATTITUDE: 2 ** 10,
    ABILITY: 2 ** 11,
    COMMUNICATION: 2 ** 12,
    MISREPRESENTED: 2 ** 13,
    UNSURE: 2 ** 14,
    EXPERIENCE: 2 ** 15,
};

const School = Base.Model.extend({
    validation: {
        name: {
            required: true,
            notags: true,
        },
        degree_major: {
            required: true,
        },
        graduation_year: {
            range: [1950, 2030],
        },
        city: {
            required: true,
            notags: true,
        },
        degree_type: {
            min: 1,
            msg: "Please select an option",
        },
        state(val) {
            if (val == "XX") {
                return "Please select an option";
            }
        },
    },
    defaults: {},
    getTitle() {
        return this.get("name");
    },
    getSubtitle() {
        return this.get("graduation_year");
    },
});

const HistoryItem = Base.Model.extend({
    comparator() {
        let ends = ["01", "01"];
        if (this.get("end_date")) {
            ends = this.get("end_date").split("/");
        }
        let starts = ["01", "01"];
        if (this.get("start_date")) {
            starts = this.get("start_date").split("/");
        }
        return parseInt(ends[1] + ends[0] + starts[1] + starts[0], 10);
    },
    getDates() {
        const start = this.get("start_date");
        const end = this.get("current_job") ? "current" : this.get("end_date");

        return `${start} - ${end}`;
    },
});

const Gap = HistoryItem.extend({
    isGap() {
        return true;
    },
});

const Job = HistoryItem.extend({
    initialize() {
        if (!this.get("reference_id")) {
            this.set("reference_id", Math.random().toString(36).slice(-3));
        }
    },
    validation: {
        start_date(val) {
            const date = moment(val, "MM/YYYY", true);
            if (!date.isValid()) {
                return "Please enter a valid date";
            }

            if (date.isAfter(moment())) {
                return "Please enter a date in the past";
            }
        },
        end_date(val, attr, state) {
            const end = moment(val, "MM/YYYY", true);
            if (!end.isValid()) {
                return "Please enter a valid date";
            }

            if (end.isAfter(moment())) {
                return "Please enter a date in the past";
            }

            const start = moment(state.start_date, "MM/YYYY", true);
            if (start.isValid() && !end.isSameOrAfter(start)) {
                return "Please enter a date after the start date";
            }
        },
        duties: {
            required: true,
            noscript: true,
        },
        city: {
            required: true,
            notags: true,
        },
        company: {
            required: true,
            notags: true,
        },
        title: {
            required: true,
            notags: true,
        },
        reason_for_leaving: {
            min: 1,
            msg: "Please select an option",
        },
        reason_explain(val) {
            if (val != undefined && val.length < 1) {
                return "Please provide details";
            }

            return Validation.validators.noscript(val);
        },
        company_explain(val) {
            if (val != undefined && val.length < 1) {
                return "Please provide at least one assignment";
            }

            return Validation.validators.noscript(val);
        },
        state(val) {
            if (val == "XX") {
                return "Please select an option";
            }
        },
    },
    defaults: {
        company: null,
        title: null,
        complete_data: true,
    },
    isGap() {
        return false;
    },
    getDuration(durationType) {
        if (!durationType) {
            durationType = "months";
        }

        const start = moment(this.get("start_date"), "MM/YYYY");
        const end = this.get("current_job") ? moment() : moment(this.get("end_date"), "MM/YYYY");

        return end.diff(start, durationType);
    },
    hasAnyKeyword(keywords) {
        if (isGutCheckTest()) {
            const fields = ["title", "company", "duties"].map((field) => this.get(field));

            // Used for fuzzy matching. See http://fusejs.io/
            const fuse = new Fuse(fields, {
                shouldSort: true,
                includeScore: true,
                threshold: 0.3,
                location: 0,
                distance: 2000,
                maxPatternLength: 64,
                minMatchCharLength: 2,
            });

            for (let i = 0; i < keywords.length; i += 1) {
                const result = fuse.search(keywords[i]);
                if (result.length > 0) {
                    return true;
                }
            }

            return false;
        }

        const regex = new RegExp(keywords.join("|"), "ig");

        const result = ["title", "company", "duties"].reduce(
            (memo, field) => memo || regex.test(this.get(field)),
            false,
        );

        return !!result;
    },
});

const Reference = Base.Model.extend({
    validation: {
        name: {
            required: true,
            notags: true,
        },
        phone(val) {
            const phone = val.replace(/\D/g, "");
            if (phone.length != 10) {
                return "Please enter a valid phone number";
            }
        },
        relationship: {
            required: true,
        },
        how_long_known: {
            required: true,
            msg: "This field is required",
        },
    },
    defaults: {
        name: null,
        phone: null,
    },
    getTitle() {
        return this.get("name");
    },
    getSubtitle() {
        return this.get("phone");
    },
});

const Resume = Base.Model.extend({
    urlRoot: "/api/v1/application",
    defaults: {
        id: null,
        had_interview: false,
        approved: false,
        state: 0,
        reason: 0,
        reason_other: "",
    },
    isApproved() {
        return this.get("approved");
    },
    isInterview() {
        return this.get("state") == APPLICATION_STATE.INTERVIEW;
    },
    isNoShow() {
        return this.get("reason") & REJECTION_REASON.NO_SHOW;
    },
    isRejected() {
        return this.get("state") == APPLICATION_STATE.REJECTED;
    },
    isApplied() {
        return this.get("state") == APPLICATION_STATE.APPLIED;
    },
    isCanceled() {
        return this.get("state") == APPLICATION_STATE.CANCELED;
    },
    isHired() {
        return this.get("state") == APPLICATION_STATE.HIRE_PENDING;
    },
});

export default {
    School,
    Job,
    Gap,
    HistoryItem,
    Reference,
    Resume,
    APPLICATION_STATE,
};
